export type UseCaseIcon =
  | "shopping-bag"
  | "map-pin"
  | "sparkles"
  | "briefcase"
  | "store"

export interface MarketingUseCase {
  slug: string
  icon: UseCaseIcon
  audience: string
  title: string
  summary: string
  challenge: string
  campaignTitle: string
  prizeIdea: string
  actions: string[]
  outcomes: string[]
  playbook: Array<{ title: string; description: string }>
  accent: string
}

export const MARKETING_USE_CASES: MarketingUseCase[] = [
  {
    slug: "ecommerce",
    icon: "shopping-bag",
    audience: "Ecommerce and retail",
    title: "Turn product launches into list-building campaigns",
    summary:
      "Showcase a prize your audience already wants, collect permission-based leads and give every entrant a reason to share.",
    challenge:
      "Product attention is expensive and short-lived. A focused giveaway can connect launch interest to an audience you can reach again.",
    campaignTitle: "New-season product bundle",
    prizeIdea: "A hero product or themed bundle",
    actions: [
      "Join the mailing list",
      "Visit the product collection",
      "Refer a friend",
    ],
    outcomes: [
      "Collect opted-in email or SMS leads",
      "Send qualified traffic to a product collection",
      "Measure referrals and campaign acquisition sources",
    ],
    playbook: [
      {
        title: "Lead with the product",
        description:
          "Use a prize that introduces the range you want customers to remember.",
      },
      {
        title: "Capture permission",
        description:
          "Ask for the minimum customer details needed for useful follow-up.",
      },
      {
        title: "Follow the campaign through",
        description:
          "Use attribution and your connected analytics to understand which promotion channels contributed.",
      },
    ],
    accent: "from-fuchsia-500 to-purple-600",
  },
  {
    slug: "hospitality-and-tours",
    icon: "map-pin",
    audience: "Hospitality and tours",
    title: "Package an experience people want to talk about",
    summary:
      "Use an experience-led prize to introduce a venue, destination or tour while building a list for future offers.",
    challenge:
      "A great experience can be difficult to communicate in a static advertisement. A campaign gives the audience a reason to picture themselves there and share it.",
    campaignTitle: "Experience for two",
    prizeIdea: "A stay, tour, meal or event package",
    actions: [
      "Join the guest list",
      "Explore the experience page",
      "Share with a travel companion",
    ],
    outcomes: [
      "Build interest around a new or off-peak experience",
      "Collect an audience for seasonal follow-up",
      "Connect online promotion with in-venue QR codes",
    ],
    playbook: [
      {
        title: "Make the prize tangible",
        description:
          "Describe what the winner will experience, not only the monetary value.",
      },
      {
        title: "Bridge offline and online",
        description:
          "Place the downloadable QR code on menus, counters, flyers or event signage.",
      },
      {
        title: "Plan the next invitation",
        description:
          "Export consenting entrants or sync them to your marketing workflow for a relevant follow-up.",
      },
    ],
    accent: "from-sky-500 to-blue-600",
  },
  {
    slug: "creators",
    icon: "sparkles",
    audience: "Creators and communities",
    title: "Give your audience more ways to participate",
    summary:
      "Bring newsletter sign-ups, content visits and referrals into one branded campaign without claiming actions a platform cannot verify.",
    challenge:
      "An audience often lives across several channels. A giveaway can provide one destination for a launch, collaboration or community milestone.",
    campaignTitle: "Community milestone giveaway",
    prizeIdea: "Merchandise, a collaboration bundle or access",
    actions: [
      "Join the newsletter",
      "Visit featured content",
      "Invite another community member",
    ],
    outcomes: [
      "Grow an audience you can contact directly",
      "Guide entrants toward selected content",
      "Reward referral reach without requiring unverified follows",
    ],
    playbook: [
      {
        title: "Choose one campaign story",
        description:
          "Anchor the giveaway to a launch, collaboration or meaningful community moment.",
      },
      {
        title: "Use accurate actions",
        description:
          "Describe profile and content visits honestly when a social platform does not provide completion verification.",
      },
      {
        title: "Keep the relationship",
        description:
          "Use email collection so the campaign can support the next piece of content too.",
      },
    ],
    accent: "from-violet-500 to-indigo-600",
  },
  {
    slug: "agencies",
    icon: "briefcase",
    audience: "Agencies and consultants",
    title: "Build repeatable campaign systems for clients",
    summary:
      "Start from a reusable structure, adapt the branding and give clients a clearer view of promotion, entries and outcomes.",
    challenge:
      "Client campaigns become expensive when every build starts from zero and reporting lives in disconnected tools.",
    campaignTitle: "Client campaign launch",
    prizeIdea: "A client product, service or partner bundle",
    actions: [
      "Capture the client’s target lead",
      "Send traffic to the priority offer",
      "Enable campaign referrals",
    ],
    outcomes: [
      "Reuse campaign patterns across client work",
      "Apply client branding and eligible custom domains",
      "Combine Promo Amp reporting with GA4, GTM or Meta Pixel",
    ],
    playbook: [
      {
        title: "Standardise the brief",
        description:
          "Define the audience, prize, primary conversion and promotion channels before design begins.",
      },
      {
        title: "Create a reusable structure",
        description:
          "Use templates and consistent action patterns to reduce repeated setup work.",
      },
      {
        title: "Report the evidence",
        description:
          "Pair campaign entries with attributed acquisition and the client’s own analytics rather than promising unsupported results.",
      },
    ],
    accent: "from-slate-700 to-slate-950",
  },
  {
    slug: "local-businesses",
    icon: "store",
    audience: "Local businesses and events",
    title: "Connect foot traffic to a measurable digital campaign",
    summary:
      "Place a campaign QR code where customers already visit and turn an in-person moment into permission-based follow-up.",
    challenge:
      "Offline attention is valuable but hard to reconnect with after a customer leaves a venue, market or event.",
    campaignTitle: "In-store customer giveaway",
    prizeIdea: "A voucher, service, event ticket or local bundle",
    actions: [
      "Scan and join the campaign",
      "Visit the business website",
      "Visit the Google review page",
    ],
    outcomes: [
      "Make entry accessible from counters, receipts and signage",
      "Attribute QR visits separately from other promotion",
      "Invite customers to a review page without claiming a submitted review",
    ],
    playbook: [
      {
        title: "Put the QR code in context",
        description:
          "Pair the code with the prize, closing date and a plain-language reason to scan.",
      },
      {
        title: "Keep entry mobile-first",
        description:
          "Ask for only the information needed so customers can enter while they are still in the moment.",
      },
      {
        title: "Separate the source",
        description:
          "Keep QR attribution enabled to distinguish offline scans from social, email and direct traffic.",
      },
    ],
    accent: "from-amber-500 to-orange-600",
  },
]

export function getMarketingUseCase(slug: string | null | undefined) {
  return MARKETING_USE_CASES.find((useCase) => useCase.slug === slug)
}
